import React from "react";

// Plain SVG line icons (replacing @mui/icons-material on redesigned pages). They inherit colour from
// `currentColor` and size from the `.ui-icon` rule in styles/ui.css.

const Icon = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
    <svg
        className={`ui-icon ${className}`}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        {children}
    </svg>
);

type IconProps = { className?: string };

export const ChevronUpIcon = ({ className }: IconProps) => (
    <Icon className={className}><path d="M6 15l6-6 6 6" /></Icon>
);

export const ChevronDownIcon = ({ className }: IconProps) => (
    <Icon className={className}><path d="M6 9l6 6 6-6" /></Icon>
);

export const ChevronLeftIcon = ({ className }: IconProps) => (
    <Icon className={className}><path d="M15 6l-6 6 6 6" /></Icon>
);

export const ChevronRightIcon = ({ className }: IconProps) => (
    <Icon className={className}><path d="M9 6l6 6-6 6" /></Icon>
);

export const ChevronsLeftIcon = ({ className }: IconProps) => (
    <Icon className={className}><path d="M11 17l-5-5 5-5M18 17l-5-5 5-5" /></Icon>
);

export const ChevronsRightIcon = ({ className }: IconProps) => (
    <Icon className={className}><path d="M13 17l5-5-5-5M6 17l5-5-5-5" /></Icon>
);

export const CloseIcon = ({ className }: IconProps) => (
    <Icon className={className}><path d="M6 6l12 12M18 6L6 18" /></Icon>
);

export const PlusIcon = ({ className }: IconProps) => (
    <Icon className={className}><path d="M12 5v14M5 12h14" /></Icon>
);

export const MinusIcon = ({ className }: IconProps) => (
    <Icon className={className}><path d="M5 12h14" /></Icon>
);

// Ozone: O3 molecule drawn as three bubbles
export const OzoneLineIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <circle cx="12" cy="6.5" r="3.5" />
        <circle cx="6.5" cy="16.5" r="3.5" />
        <circle cx="17.5" cy="16.5" r="3.5" />
    </Icon>
);

// Onzen: water drop with a sparkle
export const OnzenLineIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <path d="M10 3.5S4.5 9.5 4.5 14a5.5 5.5 0 0 0 11 0c0-4.5-5.5-10.5-5.5-10.5z" />
        <path d="M18.5 3v4M16.5 5h4" />
    </Icon>
);

export const SunIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </Icon>
);

export const MoonIcon = ({ className }: IconProps) => (
    <Icon className={className}><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></Icon>
);

export const GearIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </Icon>
);

export const RefreshIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <path d="M21 4v6h-6" />
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L21 10" />
    </Icon>
);

// Filled person glyph (spa usage pills)
export const PersonIcon = ({ className = "" }: IconProps) => (
    <svg className={`ui-icon ${className}`} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <circle cx="12" cy="7" r="4" />
        <path d="M4 21a8 8 0 0 1 16 0z" />
    </svg>
);

// Filled heart (cell life label, as on mobile)
export const HeartIcon = ({ className = "" }: IconProps) => (
    <svg className={`ui-icon ${className}`} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 21s-7.5-4.6-9.6-9.2C.9 8.4 3 4.5 6.8 4.5c2.1 0 3.6 1.1 5.2 3 1.6-1.9 3.1-3 5.2-3 3.8 0 5.9 3.9 4.4 7.3C19.5 16.4 12 21 12 21z" />
    </svg>
);

export const ThermometerIcon = ({ className }: IconProps) => (
    <Icon className={className}><path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z" /></Icon>
);

export const WifiIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <path d="M5 12.55a11 11 0 0 1 14.08 0M1.42 9a16 16 0 0 1 21.16 0M8.53 16.11a6 6 0 0 1 6.95 0" />
        <path d="M12 20h.01" />
    </Icon>
);

export const WifiOffIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <path d="M2 2l20 20M8.53 16.11a6 6 0 0 1 6.95 0M5 12.55a11 11 0 0 1 5.17-2.39M10.71 5.05A16 16 0 0 1 22.58 9M1.42 9a16 16 0 0 1 4.7-2.88M16.72 11.06A11 11 0 0 1 19 12.55" />
        <path d="M12 20h.01" />
    </Icon>
);

// Wired network: three ports joined to one line
export const EthernetIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <rect x="9" y="2" width="6" height="6" rx="1" />
        <rect x="2" y="16" width="6" height="6" rx="1" />
        <rect x="16" y="16" width="6" height="6" rx="1" />
        <path d="M12 8v4M5 16v-4h14v4" />
    </Icon>
);

export const ColumnsIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M9 3v18M15 3v18" />
    </Icon>
);

export const InfoIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4M12 8h.01" />
    </Icon>
);

export const CheckCircleIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <circle cx="12" cy="12" r="10" />
        <path d="M8 12.5l2.5 2.5L16 9.5" />
    </Icon>
);

export const WarningIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <path d="M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
        <path d="M12 9v4M12 17h.01" />
    </Icon>
);

export const LockLineIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <rect x="3" y="11" width="18" height="11" rx="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </Icon>
);

export const UnlockLineIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <rect x="3" y="11" width="18" height="11" rx="2" />
        <path d="M7 11V7a5 5 0 0 1 9.9-1" />
    </Icon>
);

export const HomeIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <path d="M3 10.5L12 3l9 7.5" />
        <path d="M5 9.5V20a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V9.5" />
    </Icon>
);

export const EyeIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
        <circle cx="12" cy="12" r="3" />
    </Icon>
);

export const EyeOffIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <path d="M9.9 5.2A10.4 10.4 0 0 1 12 5c6.5 0 10 7 10 7a17.6 17.6 0 0 1-2.9 3.9M6.6 6.6A17.4 17.4 0 0 0 2 12s3.5 7 10 7a9.7 9.7 0 0 0 5.4-1.6" />
        <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2M3 3l18 18" />
    </Icon>
);

export const SearchIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-3.5-3.5" />
    </Icon>
);

export const BellIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </Icon>
);

// Promo: a gift box
export const GiftIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <rect x="3" y="8" width="18" height="4" rx="1" />
        <path d="M12 8v13M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
        <path d="M7.5 8a2.5 2.5 0 0 1 0-5C9.5 3 12 8 12 8s2.5-5 4.5-5a2.5 2.5 0 0 1 0 5" />
    </Icon>
);

export const CopyIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <rect x="9" y="9" width="12" height="12" rx="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </Icon>
);

export const CheckIcon = ({ className }: IconProps) => (
    <Icon className={className}><path d="M5 12.5l4.5 4.5L19 7.5" /></Icon>
);

export const StoreIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <path d="M3 9l1.5-5h15L21 9M3 9h18M3 9v1.5a3 3 0 0 0 6 0V9m0 1.5a3 3 0 0 0 6 0V9m0 1.5a3 3 0 0 0 6 0V9" />
        <path d="M5 13.5V21h14v-7.5" />
    </Icon>
);

export const ChipIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <rect x="4" y="4" width="16" height="16" rx="2" />
        <rect x="9" y="9" width="6" height="6" />
        <path d="M9 1v3M15 1v3M9 20v3M15 20v3M20 9h3M20 14h3M1 9h3M1 14h3" />
    </Icon>
);

export const SlidersIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" />
    </Icon>
);

export const DropletIcon = ({ className }: IconProps) => (
    <Icon className={className}><path d="M12 2.7s-6.5 7-6.5 11.8a6.5 6.5 0 0 0 13 0C18.5 9.7 12 2.7 12 2.7z" /></Icon>
);

export const FlaskIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <path d="M9 3h6M10 3v6.5L4.5 19a1.5 1.5 0 0 0 1.3 2.2h12.4a1.5 1.5 0 0 0 1.3-2.2L14 9.5V3" />
        <path d="M7.5 15h9" />
    </Icon>
);

export const MenuIcon = ({ className }: IconProps) => (
    <Icon className={className}><path d="M4 6h16M4 12h16M4 18h16" /></Icon>
);

export const ExternalLinkIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        <path d="M15 3h6v6M10 14L21 3" />
    </Icon>
);

export const LogoutLineIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <path d="M16 17l5-5-5-5M21 12H9" />
    </Icon>
);

export const FilterLineIcon = ({ className }: IconProps) => (
    <Icon className={className}><path d="M3 4h18l-7 8.5V19l-4 2v-8.5L3 4z" /></Icon>
);

export const BoltLineIcon = ({ className }: IconProps) => (
    <Icon className={className}><path d="M13 2L4.5 13.5H12L11 22l8.5-11.5H12L13 2z" /></Icon>
);

export const LeafIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z" />
        <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
    </Icon>
);

export const AlertIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4M12 16h.01" />
    </Icon>
);

export const SnowflakeIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <path d="M12 2v20M4.93 7l14.14 10M4.93 17L19.07 7" />
        <path d="M9.5 3.5L12 6l2.5-2.5M9.5 20.5L12 18l2.5 2.5" />
    </Icon>
);

export const PencilIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </Icon>
);

// Bug: developer-only items
export const BugIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <rect x="8" y="6" width="8" height="14" rx="4" />
        <path d="M9 3l1.5 3M15 3l-1.5 3M3 13h5M16 13h5M4 8l4 2M20 8l-4 2M4 19l4-2M20 19l-4-2" />
    </Icon>
);

export const TrashIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6M14 11v6" />
    </Icon>
);

export const ChartIcon = ({ className }: IconProps) => (
    <Icon className={className}><path d="M3 3v18h18M7 15l4-4 3 3 6-7" /></Icon>
);

export const MessageIcon = ({ className }: IconProps) => (
    <Icon className={className}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></Icon>
);

export const FlagLineIcon = ({ className, filled = false }: IconProps & { filled?: boolean }) => (
    <svg className={`ui-icon ${className ?? ''}`} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor"
         strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
        <path d="M4 22v-7" fill="none" />
    </svg>
);

export const UserCogIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <circle cx="9" cy="7" r="4" />
        <path d="M2 21v-1a6 6 0 0 1 9.5-4.9" />
        <circle cx="18" cy="17" r="2.5" />
        <path d="M18 12.5v2M18 19.5v2M22.5 17h-2M15.5 17h-2" />
    </Icon>
);

export const CalendarIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M16 3v4M8 3v4M3 10h18" />
    </Icon>
);

export const ClockIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
    </Icon>
);

export const DownloadLineIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <path d="M7 10l5 5 5-5M12 15V3" />
    </Icon>
);

export const TerminalLineIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <path d="M4 17l6-6-6-6" />
        <path d="M12 19h8" />
    </Icon>
);

// Permissions / access
export const ShieldCheckIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <path d="M12 3l7 3v5c0 4.5-3 8.5-7 10-4-1.5-7-5.5-7-10V6l7-3z" />
        <path d="M9 12l2 2 4-4" />
    </Icon>
);

export const UsersIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <circle cx="9" cy="7" r="4" />
        <path d="M2 21v-1a7 7 0 0 1 14 0v1" />
        <path d="M16 3.5a4 4 0 0 1 0 7M22 21v-1a6 6 0 0 0-4-5.6" />
    </Icon>
);

// Rank / hierarchy
export const LayersIcon = ({ className }: IconProps) => (
    <Icon className={className}>
        <path d="M12 3l9 5-9 5-9-5 9-5z" />
        <path d="M3 13l9 5 9-5M3 17.5l9 5 9-5" />
    </Icon>
);
